/**
 * Single source of truth for the SP model's derived features on the frontend.
 *
 * The model is trained on three derived inputs - `enthalpy`, `hour_sin`,
 * `hour_cos` - which must stay self-consistent with the base inputs. Rather than
 * exposing them as independent sliders (which lets the user build physically
 * impossible vectors), the viewer locks them: the user sets base inputs plus a
 * single "hour of day", and these helpers recompute the derived values live.
 *
 * `computeEnthalpy` is a verbatim port of `compute_enthalpy` in
 * `backend/sp_model.py` (itself vendored from eos-online-models) - keep the two
 * in sync if the formula changes. The hour helpers mirror `get_time_features`'s
 * `sin/cos(hour * 2π / 24)` cyclical encoding.
 */

export const DERIVED_FEATURES = ['enthalpy', 'hour_sin', 'hour_cos'] as const;

/** Moist-air enthalpy (kJ/kg). T in °C, RH in %, P in hPa. Mirrors
 * `compute_enthalpy` in backend/sp_model.py. Returns NaN if inputs are
 * non-finite or humidity ≤ 0 (the formula divides by it). */
export function computeEnthalpy(
  airTemp: number,
  humidity: number,
  airPressure: number,
): number {
  if (
    !Number.isFinite(airTemp) ||
    !Number.isFinite(humidity) ||
    !Number.isFinite(airPressure) ||
    humidity <= 0
  ) {
    return NaN;
  }
  const T = airTemp;
  const RH = humidity;
  const P = airPressure;
  return (
    (1.007 * T -
      0.026 +
      ((2502 - 0.538 * T) * 0.622) /
        ((P * 100) /
          ((RH / 100) * Math.pow(10, (0.7859 + 0.03477 * T) / (1 + 0.00412 * T) + 2) -
            0.378))) /
    4.184
  );
}

/** Cyclical hour-of-day encoding for an hour in [0, 24). */
export function hourToCyclical(hour: number): { hour_sin: number; hour_cos: number } {
  const a = (hour * 2 * Math.PI) / 24;
  return { hour_sin: Math.sin(a), hour_cos: Math.cos(a) };
}

/** Recover the hour-of-day (0–23, rounded to match the model's integer hour)
 * from a loaded hour_sin/hour_cos pair. */
export function cyclicalToHour(hourSin: number, hourCos: number): number {
  if (!Number.isFinite(hourSin) || !Number.isFinite(hourCos)) return 12;
  let h = (Math.atan2(hourSin, hourCos) * 24) / (2 * Math.PI);
  if (h < 0) h += 24;
  return Math.round(h) % 24;
}
